import { ListContentIsland, TextField, ListItem, Collapse } from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { useDeferredValue, useMemo, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { ApiBeneficiary } from '../../../openapi';
import { BeneficiaryListButtonItem } from '../../ListItem/BeneficiaryListItem';

export function BeneficiaryList({
  beneficiaries,
  onSelectBeneficiary,
  isSearchEnabled = true,
}: {
  beneficiaries: ApiBeneficiary[];
  onSelectBeneficiary: (beneficiary: ApiBeneficiary) => void;
  isSearchEnabled?: boolean;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);

  const filteredBeneficiaries = useMemo(() => {
    const q = deferredQuery.trim().toLowerCase();
    if (!beneficiaries || q === '') return beneficiaries;

    return beneficiaries.filter((beneficiary) => {
      // Search by beneficiary ID
      if (beneficiary.beneficiaryId === q) return true;

      // Search by display name
      if (beneficiary.displayName?.toLowerCase().includes(q)) return true;

      // Search by type
      if (beneficiary.type?.toLowerCase().includes(q)) return true;

      // Search by currency
      if (beneficiary.currency?.toLowerCase().includes(q)) return true;

      // Search by account number
      if (beneficiary.accountNumber?.value?.toLowerCase().includes(q)) return true;

      // Type-specific searches
      if (beneficiary.type === 'INDIVIDUAL') {
        if (beneficiary.individualName?.toLowerCase().includes(q)) return true;
      }

      if (beneficiary.type === 'BUSINESS') {
        if (beneficiary.businessName?.toLowerCase().includes(q)) return true;
      }

      if (beneficiary.type === 'CRYPTO') {
        if (beneficiary.beneficiaryName?.toLowerCase().includes(q)) return true;
        if (beneficiary.accountNumber?.type?.toLowerCase().includes(q)) return true;
        if (beneficiary.subtype?.toLowerCase().includes(q)) return true;
      }

      return false;
    });
  }, [beneficiaries, deferredQuery]);

  const hasResults = (filteredBeneficiaries?.length ?? 0) > 0;

  if (!beneficiaries?.length) return null;

  return (
    <ListContentIsland
      title={t('Saved recipients')}
      slotProps={{
        list: {
          sx: { height: '100%', minHeight: 200, overflowY: 'auto' },
        },
      }}
    >
      {isSearchEnabled && (
        <ListItem>
          <TextField
            type="search"
            label={t('Search')}
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            fullWidth
          />
        </ListItem>
      )}
      {hasResults ? (
        <TransitionGroup>
          {(isSearchEnabled ? filteredBeneficiaries : beneficiaries).map((beneficiary) => (
            <Collapse key={beneficiary.beneficiaryId} in timeout={100}>
              <div>
                <BeneficiaryListButtonItem
                  beneficiary={beneficiary}
                  onClick={onSelectBeneficiary}
                />
              </div>
            </Collapse>
          ))}
        </TransitionGroup>
      ) : (
        <ListItem>{t('No recipients found')}</ListItem>
      )}
    </ListContentIsland>
  );
}
